import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { useShop } from "../context/ShopContext";
import { waLink, fetchAnnouncementBar } from "../lib/api";
import { metaTrack } from "../analytics/metaPixel";
import { LogoMark } from "./LogoMark";

const DEFAULT_ANNOUNCEMENTS = ["Authentic Ilkal Sarees", "Pan India Delivery", "Premium Handloom Collection"];

const NAV = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "New Arrivals", to: "/shop?badge=new" },
  { label: "Best Sellers", to: "/shop?badge=bestseller" },
  { label: "Ilkal Sarees", to: "/shop?category=traditional" },
  { label: "Patteda Anchu", to: "/shop?category=patteda-anchu" },
  { label: "About Us", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const Header = () => {
  const { cartCount, wishlist, setCartOpen } = useShop();
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [announcements, setAnnouncements] = useState(DEFAULT_ANNOUNCEMENTS);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAnnouncementBar()
      .then((data) => {
        if (data?.items?.length) setAnnouncements(data.items);
      })
      .catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    metaTrack("Search", { search_string: query.trim() });
    setSearchOpen(false);
    navigate(`/shop?q=${encodeURIComponent(query.trim())}`);
    setQuery("");
  };

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <div className="bg-maroon text-ivory text-center text-[11px] sm:text-xs tracking-[0.18em] uppercase py-2 px-4 overflow-hidden" data-testid="announcement-bar">
        <div className="flex whitespace-nowrap animate-marquee w-max">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="inline-flex items-center">
              {announcements.map((line, j) => (
                <React.Fragment key={j}>
                  <span className="mx-10">{line}</span>
                  <span className="text-gold">•</span>
                </React.Fragment>
              ))}
            </span>
          ))}
        </div>
      </div>

      <div className="bg-ivory/85 backdrop-blur-xl border-b border-gold/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 sm:h-20 gap-4">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 -ml-2 text-charcoal" data-testid="mobile-menu-btn" aria-label="Menu">
                <Menu className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-ivory w-80 p-0">
              <div className="p-6 border-b border-gold/30">
                <p className="font-heading text-2xl text-maroon">R V ILKAL SAREES</p>
                <p className="text-xs tracking-[0.2em] uppercase text-gold mt-1">Ilkal • Karnataka</p>
              </div>
              <nav className="flex flex-col p-6 gap-1" data-testid="mobile-nav">
                {NAV.map((n) => (
                  <Link
                    key={n.label}
                    to={n.to}
                    onClick={() => setMenuOpen(false)}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="py-3 text-sm tracking-wide uppercase text-charcoal/80 hover:text-maroon border-b border-gold/10"
                  >
                    {n.label}
                  </Link>
                ))}
                <Link
                  to="/wishlist"
                  onClick={() => setMenuOpen(false)}
                  data-testid="mobile-nav-wishlist"
                  className="py-3 text-sm tracking-wide uppercase text-charcoal/80 hover:text-maroon border-b border-gold/10"
                >
                  Wishlist
                </Link>
              </nav>
            </SheetContent>
          </Sheet>

          <Link to="/" className="flex items-center gap-2.5 select-none shrink-0 min-w-0" data-testid="logo-link">
            <LogoMark className="w-8 h-8 sm:w-10 sm:h-10 shrink-0" />
            <span className="flex flex-col items-start min-w-0">
              <span className="font-heading text-base sm:text-2xl tracking-wide text-maroon leading-none whitespace-nowrap">R V ILKAL SAREES</span>
              <span className="hidden sm:block text-[10px] tracking-[0.3em] uppercase text-gold mt-1">Woven with Tradition</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
            {NAV.slice(1).map((n) => (
              <NavLink
                key={n.label}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-[13px] tracking-[0.12em] uppercase text-charcoal/70 hover:text-maroon transition-colors whitespace-nowrap"
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1 sm:gap-2">
            <button
              className="p-2 text-charcoal hover:text-maroon transition-colors"
              data-testid="search-toggle-btn"
              aria-label="Search"
              onClick={() => setSearchOpen((s) => !s)}
            >
              {searchOpen ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Search className="w-5 h-5" strokeWidth={1.5} />}
            </button>

            <Popover>
              <PopoverTrigger asChild>
                <button className="hidden sm:block p-2 text-charcoal hover:text-maroon transition-colors" data-testid="account-btn" aria-label="Account">
                  <User className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 bg-ivory border-gold/40 rounded-sm">
                <p className="font-heading text-lg text-charcoal">Your Account</p>
                <p className="text-sm text-charcoal/60 mt-2 leading-relaxed">
                  Online accounts are coming soon. For orders and tracking, message us on WhatsApp and our team will help you right away.
                </p>
                <a
                  href={waLink("Namaste! I would like help with my R V Ilkal Sarees order.")}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="account-whatsapp-link"
                  className="mt-4 block text-center bg-maroon text-ivory text-xs tracking-[0.15em] uppercase py-3 hover:bg-maroon-dark transition-colors"
                >
                  Message on WhatsApp
                </a>
              </PopoverContent>
            </Popover>

            <Link to="/wishlist" className="relative hidden sm:flex p-2 text-charcoal hover:text-maroon transition-colors" data-testid="wishlist-link" aria-label="Wishlist">
              <Heart className="w-5 h-5" strokeWidth={1.5} />
              {wishlist.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-gold text-charcoal text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-semibold" data-testid="wishlist-count">
                  {wishlist.length}
                </span>
              )}
            </Link>

            <button className="relative p-2 text-charcoal hover:text-maroon transition-colors" data-testid="cart-btn" aria-label="Shopping bag" onClick={() => setCartOpen(true)}>
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-maroon text-ivory text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-semibold" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-gold/20 bg-ivory/95" data-testid="search-bar">
            <form onSubmit={submitSearch} className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
              <Search className="w-4 h-4 text-gold" strokeWidth={1.5} />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search sarees — silk, cotton, bridal, temple border…"
                data-testid="search-input"
                className="flex-1 bg-transparent outline-none text-sm text-charcoal placeholder:text-charcoal/40"
              />
              <button type="submit" data-testid="search-submit-btn" className="text-xs tracking-[0.15em] uppercase text-maroon hover:text-maroon-dark">
                Search
              </button>
            </form>
          </div>
        )}
      </div>
    </header>
  );
};
